import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Card, Col, Empty, Input, Row, Typography } from "antd";
import {
    SettingOutlined, SearchOutlined, RightOutlined,
    FieldNumberOutlined, ThunderboltOutlined, FileTextOutlined,
    HeartOutlined, ProfileOutlined, CalculatorOutlined, FileDoneOutlined,
    DollarOutlined, SmileOutlined, NumberOutlined, FieldTimeOutlined,
    SafetyOutlined, AuditOutlined, BankOutlined, ShopOutlined, ApartmentOutlined,
    TeamOutlined,
} from "@ant-design/icons";
import Footer from "../../components/Footer";
import NavBar from "../../components/NavBar";
import SideBar from "../../components/SideBar";

const { Text, Title } = Typography;
const PRIMARY = "#242166";

// Paramètres regroupés par thème (style aligné sur /tarification/v2).
const GROUPS = [
    {
        key: "sinistre",
        label: "Sinistre & déclaration",
        items: [
            { title: "Numéro de sinistre", description: "Format et règles du numéro de sinistre.", link: "/autre-parametres/numero-sinistre", button: "Configurer", icon: <FieldNumberOutlined />, color: PRIMARY },
            { title: "Causes de sinistre", description: "Différentes causes possibles d'un sinistre.", link: "/autre-parametres/cause-sinistres", button: "Gérer", icon: <ThunderboltOutlined />, color: "#dc2626" },
            { title: "Types de pièces", description: "Types de pièces justificatives des dossiers sinistres.", link: "/autre-parametres/type-pieces", button: "Gérer", icon: <FileTextOutlined />, color: "#0891b2" },
        ],
    },
    {
        key: "evaluation",
        label: "Évaluation & indemnisation",
        items: [
            { title: "Préjudices", description: "Préjudices indemnisables pris en compte dans l'évaluation.", link: "/autre-parametres/prejudices", button: "Gérer", icon: <HeartOutlined />, color: "#7c3aed" },
            { title: "Natures d'actes", description: "Requalifiez les actes en « Mécanique / Matériel » ou « Médical » : chaque prise en charge ne propose que les actes de sa discipline.", link: "/autre-parametres/nature-acte", button: "Gérer", icon: <ProfileOutlined />, color: "#16a34a" },
            { title: "Motifs de règlement", description: "Libellés des motifs proposés à l'encaissement des règlements. Ajout, modification et suppression.", link: "/parametres/motifs-reglement", button: "Gérer les motifs", icon: <FileDoneOutlined />, color: "#2563eb" },
            { title: "Signataires des documents", description: "Titres, ordre et nom des signataires sur l'ordre de dépense, la fiche de demande de règlement et la quittance (propres à votre organisme).", link: "/parametres/signataires-documents", button: "Paramétrer", icon: <TeamOutlined />, color: "#0891b2" },
            { title: "Barème d'évaluation", description: "Barème d'évaluation appliqué à l'ouverture d'un dossier sinistre.", link: "/autre-parametres/bareme-evaluation", button: "Définir", icon: <CalculatorOutlined />, color: "#d97706" },
            { title: "Somme minimale à payer", description: "Montant minimal de règlement d'un sinistre.", link: "/autre-parametres/somme-minimale-a-payer", button: "Définir", icon: <DollarOutlined />, color: "#16a34a" },
            { title: "Pretium Doloris", description: "Barème du pretium doloris (souffrances endurées).", link: "/parametres/pretium-doloris", button: "Définir", icon: <SmileOutlined />, color: "#7c3aed" },
        ],
    },
    {
        key: "rentes",
        label: "Rentes & valeurs de référence",
        items: [
            { title: "SMIG", description: "Salaire minimum interprofessionnel garanti.", link: "/parametres/smig", button: "Définir", icon: <DollarOutlined />, color: PRIMARY },
            { title: "Valeur du point IP", description: "Valeur du point d'incapacité permanente.", link: "/parametres/ajouter-valeur-point-ip", button: "Définir", icon: <NumberOutlined />, color: "#0891b2" },
            { title: "Âge limite de paiement de rente", description: "Âge limite pour le paiement des rentes.", link: "/parametres/age-limite-paiement-rente", button: "Définir", icon: <FieldTimeOutlined />, color: "#d97706" },
        ],
    },
    {
        key: "acteurs",
        label: "Acteurs & partenaires",
        items: [
            { title: "Experts", description: "Experts disponibles pour les dossiers sinistres.", link: "/autre-parametres/experts", button: "Gérer", icon: <SafetyOutlined />, color: "#16a34a" },
            { title: "Points d'investigation", description: "Rubriques et points cochables du Mandat d'Investigation.", link: "/autre-parametres/points-investigation", button: "Gérer", icon: <AuditOutlined />, color: "#0f766e" },
            { title: "Documents à réclamer", description: "Documents cochables du bloc « Vous voudrez bien nous adresser » de l'attestation de prise en charge.", link: "/autre-parametres/documents-a-reclamer", button: "Gérer", icon: <FileTextOutlined />, color: "#242166" },
            { title: "Entités de constat", description: "Entités habilitées à établir un constat.", link: "/autre-parametres/entites-constat", button: "Gérer", icon: <AuditOutlined />, color: "#0891b2" },
            { title: "Conseils juridiques", description: "Conseils juridiques liés aux dossiers sinistres.", link: "/parametres/conseils-juridiques", button: "Gérer", icon: <BankOutlined />, color: "#7c3aed" },
            { title: "Juridictions", description: "Tribunaux utilisés pour la mise en contentieux.", link: "/parametres/juridictions", button: "Gérer", icon: <BankOutlined />, color: PRIMARY },
            { title: "Établissements", description: "Établissements de soins / garages partenaires.", link: "/autre-parametres/etablisements", button: "Gérer", icon: <ShopOutlined />, color: "#dc2626" },
            { title: "Types d'établissement", description: "Types d'établissement utilisés dans les sinistres.", link: "/autre-parametres/type-etablissements", button: "Gérer", icon: <ApartmentOutlined />, color: "#d97706" },
        ],
    },
];

const ParametresSinistres = () => {
    const navigate = useNavigate();
    const [search, setSearch] = useState("");

    const filteredGroups = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return GROUPS;
        return GROUPS
            .map((g) => ({ ...g, items: g.items.filter((i) => `${i.title} ${i.description}`.toLowerCase().includes(q)) }))
            .filter((g) => g.items.length);
    }, [search]);

    return (
        <div className="layout-wrapper layout-content-navbar">
            <div className="layout-container">
                <SideBar currentMenu={"PARAMETRES_SINISTRES"} />
                <div className="layout-page">
                    <NavBar />
                    <div className="content-wrapper">
                        <div className="container-xxl flex-grow-1 container-p-y">

                            {/* En-tête */}
                            <div style={{ background: `linear-gradient(135deg, ${PRIMARY} 0%, #1890ff 100%)`, borderRadius: 14, padding: "18px 22px", marginBottom: 20, color: "#fff", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
                                <div>
                                    <Title level={4} style={{ margin: 0, color: "#fff" }}>
                                        <SettingOutlined style={{ marginRight: 8 }} />Paramètres des sinistres
                                    </Title>
                                    <Text style={{ color: "rgba(255,255,255,0.85)" }}>
                                        Déclaration, évaluation, indemnisation, rentes et acteurs des dossiers sinistres
                                    </Text>
                                </div>
                                <Input
                                    allowClear
                                    prefix={<SearchOutlined style={{ color: "rgba(255,255,255,0.7)" }} />}
                                    placeholder="Rechercher un paramètre…"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    style={{ maxWidth: 320, background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.3)", borderRadius: 8 }}
                                    styles={{ input: { background: "transparent", color: "#fff" } }}
                                />
                            </div>

                            {/* Groupes de cartes */}
                            {filteredGroups.length === 0 ? (
                                <Empty description="Aucun paramètre trouvé" style={{ padding: "50px 0" }} />
                            ) : filteredGroups.map((g) => (
                                <div key={g.key} style={{ marginBottom: 22 }}>
                                    <div style={{ fontSize: 12, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 10 }}>{g.label}</div>
                                    <Row gutter={[16, 16]}>
                                        {g.items.map((item) => (
                                            <Col xs={24} sm={12} lg={8} xxl={6} key={item.title}>
                                                <Card hoverable bordered={false} onClick={() => navigate(item.link)}
                                                    style={{ borderRadius: 14, boxShadow: "0 2px 10px rgba(0,0,0,0.06)", height: "100%" }} bodyStyle={{ padding: 18, display: "flex", flexDirection: "column", height: "100%" }}>
                                                    <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
                                                        <span style={{ width: 42, height: 42, borderRadius: 11, background: `${item.color}14`, color: item.color, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0 }}>{item.icon}</span>
                                                        <div style={{ fontWeight: 700, color: "#111827", fontSize: 15 }}>{item.title}</div>
                                                    </div>
                                                    <Text type="secondary" style={{ fontSize: 13, flex: 1 }}>{item.description}</Text>
                                                    <div style={{ marginTop: 14 }}>
                                                        <Button type="link" style={{ padding: 0, color: item.color, fontWeight: 600 }}>{item.button} <RightOutlined /></Button>
                                                    </div>
                                                </Card>
                                            </Col>
                                        ))}
                                    </Row>
                                </div>
                            ))}
                        </div>
                        <Footer />
                        <div className="content-backdrop fade"></div>
                    </div>
                </div>
            </div>
            <div className="layout-overlay layout-menu-toggle"></div>
            <div className="drag-target"></div>
        </div>
    );
};

export default ParametresSinistres;
